import type { AutocompleteSearch } from '@/types/Autocomplete'
import {
	fetchSuggestions,
	getSuggestionLabel,
	matchSearchRules,
} from '@/util/autocomplete'
import { fuzzyIncludes } from '@/util/fuzzy'
import type { ModuleObject } from '@/util/modules'
import type { AutocompleteResult } from '@common/types/AutocompleteResult'
import type { ConfigSearch } from '@common/types/config/Search'
import type { Poi } from '@common/types/Poi'
import { useEqStable, useFollow, useObserve } from '@lc/libreact'
import { eq, throttle } from '@lc/libutils'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDataConfigSearch } from './data/useDataConfigSearch'
import { useExecutor } from './useExecutor'
import { useModule } from './useModule'

function defineSearchResults(
	configSearch: ConfigSearch,
	results: AutocompleteResult[],
	currentModule: ModuleObject,
): (AutocompleteResult & { def: string })[] {
	return results
		.map(result => ({
			def: matchSearchRules(result, configSearch.defs, currentModule)
				?.def,
			...result,
		}))
		.filter(
			(result): result is AutocompleteResult & { def: string } =>
				typeof result.def === 'string',
		)
}

export function useNewAutocomplete({
	search: initialSearch,
	types: typesUnstable,
	localItems: localItemsUnstable,
	poiCategories,
}: {
	search: string
	types: AutocompleteSearch[]
	localItems?: AutocompleteResult[] | Poi[]
	poiCategories?: string[]
}): {
	search: string
	setSearch: (search: string) => void
	results: (AutocompleteResult & { def: string })[]
} {
	const [search, setSearch] = useState(initialSearch)
	useEffect(() => {
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setSearch(initialSearch)
	}, [initialSearch])

	const { configSearch } = useDataConfigSearch({
		level: 0,
		disable: !search,
	})
	const configSearchFollow = useFollow(configSearch)
	const configSearchObserve = useObserve(configSearch)
	const currentModule = useModule()
	const currentModuleFollow = useFollow(currentModule)

	const types = useEqStable(typesUnstable)
	const localNormalizedItems = useMemo<AutocompleteResult[]>(() => {
		if (!localItemsUnstable?.length) return []
		return localItemsUnstable.map(item => {
			if ('type' in item) return item
			else return { type: 'poi', poi: item, weight: 0 }
		})
	}, [localItemsUnstable])
	const localItems = useFollow(localNormalizedItems)

	const [results, setResults] = useState<
		(AutocompleteResult & { def: string })[]
	>([])
	const exe = useExecutor()
	const ordering = useRef(0)
	const update = useRef(
		throttle(
			(
				search: string,
				types: AutocompleteSearch[],
				poiCategories?: string[],
			) =>
				exe.schedule(
					{ level: 0, name: `search(${JSON.stringify(search)})` },
					async () => {
						const key = ++ordering.current
						let configSearch = configSearchFollow.current
						let results: AutocompleteResult[] = []
						let remote: AutocompleteResult[] = []
						if (search) {
							while (!configSearch) {
								configSearch = await configSearchObserve.wait({
									signal: AbortSignal.timeout(1000),
								})
							}
							if (
								!localItems.current.length &&
								types.length !== 0
							) {
								remote = await fetchSuggestions(
									search,
									types,
									poiCategories,
								)
							}
							const local =
								localItems.current?.filter(item =>
									fuzzyIncludes(
										search,
										getSuggestionLabel(item),
									),
								) ?? []
							results = [...remote, ...local]
						}
						if (ordering.current !== key) return
						const defineResults = defineSearchResults(
							configSearch!,
							results,
							currentModuleFollow.current,
						)
						setResults(prev =>
							eq(defineResults, prev) ? prev : defineResults,
						)
					},
				),
			500,
		),
	)
	useEffect(() => {
		update.current(search, types, poiCategories)
	}, [search, types, poiCategories])

	return { search, setSearch, results }
}
